import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { productIntegrations } from '../../config/productIntegrations';
import { getStoredAuthToken } from '../../utils/authSession';
import './MatrixConsoleModal.css';

const DEFAULT_CONSOLE_URL = productIntegrations.matrixConsoleUrl;

function getActiveSubscriptions(subscriptions = []) {
  return subscriptions.filter(
    (subscription) => String(subscription?.status || '').toLowerCase() === 'active'
  );
}

function getSubscriptionLabel(subscription) {
  return (
    subscription?.tier ||
    subscription?.planName ||
    subscription?.plan?.name ||
    subscription?.name ||
    subscription?.productName ||
    subscription?.price?.nickname ||
    'Active subscription'
  );
}

function buildConsoleIdentity({ user, seller, currentProfile, subscriptions, mode }) {
  const userId = user?._id || null;
  const sellerId = seller?._id || null;
  const profileId = currentProfile === 'User' ? userId : sellerId || userId;
  const activeSubscriptions = getActiveSubscriptions(subscriptions);
  const subscriptionLabels = activeSubscriptions.map(getSubscriptionLabel);
  const displayName =
    user?.name || user?.displayName || user?.username || user?.handle || 'MATRIX Member';

  return {
    source: 'buzzvibe',
    sessionKey: `buzzvibe:matrix-console:${profileId || userId || 'guest'}`,
    openedAt: new Date().toISOString(),
    user: {
      id: userId,
      email: user?.email || null,
      username: user?.username || user?.handle || null,
      displayName,
    },
    profile: {
      currentProfile,
      profileId,
      sellerId,
    },
    analytics: {
      userId,
      sellerId,
      profileId,
      subscriptionLabels,
    },
    dashboards: {
      currentProfile,
      activeSubscriptions: subscriptionLabels,
      defaultView: 'overview',
    },
    settings: {
      mode,
      selectedProfile: currentProfile,
    },
    preferences: {
      experience: 'immersive-console',
      mode,
    },
  };
}

function buildConsoleUrl(identity, view = 'overview') {
  const url = new URL(DEFAULT_CONSOLE_URL);
  url.searchParams.set('source', identity.source);
  url.searchParams.set('profile', identity.profile.currentProfile.toLowerCase());
  url.searchParams.set('profileId', identity.profile.profileId || '');
  url.searchParams.set('sessionKey', identity.sessionKey);
  url.searchParams.set('view', view);
  return url.toString();
}

export default function MatrixConsoleModal({
  isOpen,
  onClose,
  user,
  seller,
  currentProfile,
  subscriptions,
  mode,
}) {
  const iframeRef = useRef(null);
  const consoleIdentity = useMemo(
    () => buildConsoleIdentity({ user, seller, currentProfile, subscriptions, mode }),
    [currentProfile, mode, seller, subscriptions, user]
  );
  const consoleUrl = useMemo(
    () => buildConsoleUrl(consoleIdentity, 'overview'),
    [consoleIdentity]
  );
  const consoleOrigin = useMemo(() => new URL(consoleUrl).origin, [consoleUrl]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(consoleIdentity.sessionKey, JSON.stringify(consoleIdentity));
    } catch (error) {
      console.warn('Unable to persist console context', error);
    }
  }, [consoleIdentity, isOpen]);

  const postConsoleContext = () => {
    if (!iframeRef.current?.contentWindow) {
      return;
    }

    const token = getStoredAuthToken();

    iframeRef.current.contentWindow.postMessage(
      {
        type: 'buzzvibe:console-context',
        payload: {
          ...consoleIdentity,
          section: 'overview',
          token,
        },
      },
      consoleOrigin
    );
  };

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="matrix-console-overlay" role="dialog" aria-modal="true" aria-label="Matrix console">
      <div className="matrix-console-shell">
        <div className="matrix-console-header">
          <div className="matrix-console-header-copy">
            <span className="matrix-console-header-title">Matrix Gateway Console</span>
          </div>
          <div className="matrix-console-actions">
            <a
              className="matrix-console-link"
              href={consoleUrl}
              target="_blank"
              rel="noreferrer"
            >
              <ArrowTopRightOnSquareIcon width={18} height={18} />
              Open standalone
            </a>
            <button type="button" className="matrix-console-close" onClick={onClose} aria-label="Close console">
              <XMarkIcon width={20} height={20} />
            </button>
          </div>
        </div>

        <div className="matrix-console-main">
          <div className="matrix-console-frame-shell">
            <iframe
              ref={iframeRef}
              title="Matrix console"
              src={consoleUrl}
              className="matrix-console-frame"
              allow="clipboard-read; clipboard-write"
              onLoad={postConsoleContext}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}